/* Basis — advertorial behaviour.
 *
 * Progressive enhancement only. Every section is laid out, readable and
 * navigable with this file absent: the scrollers are CSS scroll-snap tracks,
 * the videos carry `controls`, and every CTA is a plain <a href>. Nothing here
 * is required for display, which is the acceptance test that separates a
 * native build from a page-builder one (Website/HANDOFF.md).
 *
 * What it adds:
 *   1. Dots for the horizontal card scrollers. basis-native.js already gives
 *      those tracks drag, arrows, thumbnail sync and keyboard; it does not
 *      draw dots, so that is the gap this fills.
 *   2. A sound toggle on the muted autoplay videos.
 *   3. dataLayer events for CTA clicks and scroll depth, which is how this
 *      page gets judged: GA4 landing_page plus CTA-click rate, read against
 *      downstream add-to-cart on the PDP.
 *
 * Carousel division of labour, per _System/Playbooks/carousel-standard.md:
 * CSS owns geometry. This file reads the track and publishes which slide is
 * active. It never sets a width.
 */
(function () {
  'use strict';

  if (window.__bnAdvInit) return;
  window.__bnAdvInit = true;

  var dl = function (payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /* ------------------------------------------------------------------ *
   * Dots
   *
   * One dot per reachable scroll position, which on a track of equal-width
   * slides is one per slide. Dots are indicators, not controls, for the same
   * reason the CSS hides them from 768px up: above that breakpoint the track
   * becomes a grid with no scroll, so a dot would point at nothing.
   * ------------------------------------------------------------------ */
  function wireDots(scope) {
    var track = scope.querySelector('[data-bn-carousel]');
    var host = scope.querySelector('[data-bnadv-dots]');
    if (!track || !host || host.__bnAdvDots) return;
    host.__bnAdvDots = true;

    var slides = [].slice.call(track.children);
    if (slides.length < 2) { host.hidden = true; return; }

    slides.forEach(function (_, i) {
      var d = document.createElement('i');
      if (i === 0) d.className = 'on';
      host.appendChild(d);
    });

    var dots = host.children;

    function sync() {
      // Nearest slide centre to the viewport centre. Matches what the reader
      // perceives as "the one I'm looking at" when a neighbour peeks in.
      var mid = track.scrollLeft + track.clientWidth / 2;
      var best = 0, bestD = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var c = slides[i].offsetLeft - track.offsetLeft + slides[i].offsetWidth / 2;
        var d = Math.abs(c - mid);
        if (d < bestD) { bestD = d; best = i; }
      }
      for (var j = 0; j < dots.length; j++) {
        dots[j].className = j === best ? 'on' : '';
      }
    }

    track.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * Sound toggle
   *
   * The videos ship `muted autoplay playsinline` as parse-time attributes.
   * iOS honours autoplay only for a muted inline video whose attributes say
   * so before script runs; a JS play() call is refused, which is why driving
   * this from script passes in a desktop WebKit harness and fails on a real
   * handset. Same contract documented in sections/bn-product-module.liquid.
   *
   * Unmuting is a user gesture, so play() is allowed here.
   * ------------------------------------------------------------------ */
  function wireSound(wrap) {
    var v = wrap.querySelector('video');
    var btn = wrap.querySelector('[data-bnadv-snd]');
    if (!v || !btn || btn.__bnAdvSnd) return;
    btn.__bnAdvSnd = true;

    var labelOn = btn.getAttribute('data-label-on') || 'Tap for sound';
    var labelOff = btn.getAttribute('data-label-off') || 'Tap to mute';

    btn.addEventListener('click', function () {
      v.muted = !v.muted;
      if (!v.muted) {
        v.currentTime = 0;
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      }
      btn.textContent = v.muted ? labelOn : labelOff;
      btn.setAttribute('aria-pressed', String(!v.muted));
    });
  }

  /* ------------------------------------------------------------------ *
   * Click the picture to play it
   *
   * Every video player a reader has used (YouTube, Vimeo, the native player
   * on a phone) toggles playback when you click the frame. Leaving that to the
   * control bar alone means the obvious target does nothing, which reads as a
   * broken video rather than a design decision.
   *
   * `controls` stays on, because it is the JavaScript-off fallback and the
   * only keyboard-reachable transport. That creates one problem: a click on
   * the native control bar also lands on the video element, so a naive
   * handler fires twice and cancels itself. The bottom band is therefore
   * excluded by height. 46px covers the WebKit and Blink control bars with
   * room to spare; a click inside it is the browser's to handle.
   *
   * A muted autoplaying clip is a different gesture: the reader is not asking
   * it to stop, they are asking to hear it. So the first click unmutes and
   * restarts rather than pausing.
   * ------------------------------------------------------------------ */
  var CONTROL_BAR_PX = 46;

  function wireClickToPlay(wrap) {
    var v = wrap.querySelector('video');
    if (!v || wrap.__bnAdvClick) return;
    wrap.__bnAdvClick = true;

    /* A listener on the <video> itself does not work. WebKit's native control
       overlay is a shadow-root layer that spans the whole video surface and
       swallows the pointer event, so the click never reaches our handler.
       Measured on staging: three videos, three clicks on the frame, no state
       change on any of them.

       So the click surface is our own layer, sitting above the video and
       stopping short of the control bar. It is created here rather than in
       Liquid so that with JavaScript off the markup is a plain <video
       controls> and nothing covers it. */
    var hit = document.createElement('button');
    hit.type = 'button';
    hit.className = 'bnadv__hit';
    hit.style.bottom = CONTROL_BAR_PX + 'px';

    var autoplay = v.hasAttribute('autoplay');
    hit.setAttribute('aria-label', autoplay ? 'Unmute video' : 'Play video');
    if (!autoplay) hit.classList.add('is-idle');

    function sync() {
      hit.classList.toggle('is-idle', v.paused);
      hit.setAttribute('aria-label', v.paused ? 'Play video' : 'Pause video');
    }
    v.addEventListener('play', sync);
    v.addEventListener('pause', sync);

    /* Two different gestures behind one click, decided by whether the clip has
       audio worth hearing.

       A silent demo loop (bands versus the trainer) has no sound toggle. There
       is nothing to unmute, and what a reader actually wants is to stop it on
       a frame and look at the movement, so click pauses.

       A clip with a voice has a sound toggle, and a muted autoplaying video
       with people talking in it is a video asking to be unmuted. */
    var hasAudio = !!wrap.querySelector('[data-bnadv-snd]');

    hit.addEventListener('click', function () {
      if (v.muted && autoplay && hasAudio) {
        v.muted = false;
        v.currentTime = 0;
        var p1 = v.play();
        if (p1 && p1.catch) p1.catch(function () {});
        hit.setAttribute('aria-label', 'Pause video');
        var btn = wrap.querySelector('[data-bnadv-snd]');
        if (btn) {
          btn.textContent = btn.getAttribute('data-label-off') || 'Tap to mute';
          btn.setAttribute('aria-pressed', 'true');
        }
        return;
      }
      if (v.paused) {
        var p2 = v.play();
        if (p2 && p2.catch) p2.catch(function () {});
      } else {
        v.pause();
      }
    });

    wrap.appendChild(hit);
    sync();
  }

  /* ------------------------------------------------------------------ *
   * Carry the campaign forward
   *
   * Measured on staging: clicking through to the product page keeps the GA4
   * session id, `_shopify_s` and `_fbc` intact, so a purchase in that session
   * is already attributed to the campaign without any of this. Session
   * attribution is captured once, at landing, and persists.
   *
   * The case it does not cover is the one this page invites. GA4 ends a
   * session after 30 minutes of inactivity. This article is ~13,000px; a
   * reader who takes longer than that and then clicks starts a NEW session,
   * and the new session's source is a same-domain referral rather than the
   * campaign. On a long-form advertorial that is not an edge case, it is the
   * attentive reader, and losing exactly those conversions would understate
   * the funnel that produced them.
   *
   * So the marketing params ride along on the click and re-stamp the new
   * session. Whitelisted rather than "copy the whole query string", so nothing
   * unexpected is forwarded; params already on the destination win, so a
   * hand-built link is never overwritten. Applied on click rather than at load
   * so a link added later still gets it, and so the href a reader sees in the
   * status bar stays clean until they use it.
   * ------------------------------------------------------------------ */
  var CARRY = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'twclid',
    'li_fat_id', 'epik', 'irclickid', 'rdt_cid', 'sccid', 'srsltid', 'ScCid',
    'ref', 'referrer',
    // staging only: keeps preview navigation on the theme under test
    'preview_theme_id', 'view'
  ];

  function carriedParams() {
    var src = new URLSearchParams(location.search);
    var out = [];
    for (var i = 0; i < CARRY.length; i++) {
      var k = CARRY[i];
      if (src.has(k)) out.push([k, src.get(k)]);
    }
    return out;
  }

  function wireCampaignCarry() {
    var carry = carriedParams();
    if (!carry.length) return;

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var a = e.target.closest('a[href]');
      if (!a) return;

      var raw = a.getAttribute('href') || '';
      if (raw.charAt(0) === '#' || /^(mailto:|tel:|javascript:)/i.test(raw)) return;

      var url;
      try { url = new URL(a.href, location.origin); } catch (err) { return; }
      if (url.origin !== location.origin) return;          // same site only

      var changed = false;
      for (var i = 0; i < carry.length; i++) {
        if (!url.searchParams.has(carry[i][0])) {
          url.searchParams.set(carry[i][0], carry[i][1]);
          changed = true;
        }
      }
      if (changed) a.setAttribute('href', url.pathname + url.search + url.hash);
    }, true);   // capture, so the href is rewritten before navigation starts
  }

  /* ------------------------------------------------------------------ *
   * Zoomable figures
   *
   * The markup is already a working link to the full-size file, so this only
   * has to intercept the click and show the same image in a <dialog>. Native
   * <dialog> is used rather than a hand-rolled overlay because showModal()
   * brings focus trapping, Escape-to-close, inert background and a ::backdrop
   * with no code of our own; all of that is what makes a lightbox accessible
   * and all of it is easy to get wrong by hand.
   *
   * If anything here throws or <dialog> is unsupported, the click is never
   * intercepted and the browser follows the href, which is the same image.
   * ------------------------------------------------------------------ */
  function wireZoom() {
    var links = document.querySelectorAll('[data-bnadv-zoom]');
    if (!links.length) return;
    if (typeof HTMLDialogElement === 'undefined') return;   // fall through to the href

    var dlg = null;

    function build() {
      dlg = document.createElement('dialog');
      dlg.className = 'bnadv__lightbox';
      dlg.innerHTML =
        '<button type="button" class="bnadv__lightbox-x" aria-label="Close">\u00d7</button>' +
        '<img alt="">';
      document.body.appendChild(dlg);
      dlg.addEventListener('click', function (e) {
        // click outside the image closes, matching the backdrop convention
        if (e.target === dlg || e.target.classList.contains('bnadv__lightbox-x')) dlg.close();
      });
      return dlg;
    }

    Array.prototype.forEach.call(links, function (a) {
      a.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;  // let people open a new tab
        var src = a.getAttribute('href');
        var img = a.querySelector('img');
        if (!src) return;
        e.preventDefault();
        var d = dlg || build();
        var full = d.querySelector('img');
        full.src = src;
        full.alt = img ? img.getAttribute('alt') || '' : '';
        d.showModal();
        dl({ event: 'adv_zoom_open', adv_page: location.pathname, adv_zoom_src: src });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * CTA clicks
   *
   * Delegated, so CTAs rendered by any bn-adv section are covered without
   * each section wiring its own listener.
   *
   * The marker sits on the anchor itself for a button CTA and on the wrapper
   * for an inline-link CTA, because an inline link is authored inside rich
   * text and Liquid cannot reach into it to add an attribute. closest() from
   * the anchor resolves both: it returns the anchor when the anchor carries
   * the marker, and walks up to the wrapper when it does not.
   *
   * `position` is authored rather than derived from DOM order. Section order
   * differs between the editorial and direct-response templates, and a
   * position that shifts underneath a live test makes the arms incomparable.
   * ------------------------------------------------------------------ */
  function wireCta() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var a = e.target.closest('a[href]');
      if (!a) return;
      var marker = a.closest('[data-bnadv-cta]');
      if (!marker) return;
      dl({
        event: 'adv_cta_click',
        adv_page: location.pathname,
        adv_cta_position: marker.getAttribute('data-bnadv-cta') || 'unknown',
        adv_cta_style: marker.getAttribute('data-bnadv-style') || 'link',
        adv_cta_href: a.getAttribute('href') || ''
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Scroll depth
   *
   * Depth on a long page is the read signal: it separates "the ad sold a
   * click" from "the page held them", which is the whole reason a pre-sell
   * page exists. Thresholds fire once each.
   * ------------------------------------------------------------------ */
  function wireDepth() {
    var marks = [25, 50, 75, 100];
    var fired = {};

    function check() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;
      var pct = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
      for (var i = 0; i < marks.length; i++) {
        var m = marks[i];
        if (pct >= m && !fired[m]) {
          fired[m] = true;
          dl({
            event: 'adv_scroll_depth',
            adv_page: location.pathname,
            adv_depth: m
          });
        }
      }
      if (fired[100]) window.removeEventListener('scroll', onScroll);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; check(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    check();
  }

  function init() {
    [].forEach.call(document.querySelectorAll('[data-bnadv-scroller]'), wireDots);
    [].forEach.call(document.querySelectorAll('[data-bnadv-vid]'), wireSound);
    // every video, autoplaying or not
    [].forEach.call(document.querySelectorAll('.bnadv__vid'), wireClickToPlay);
    wireCampaignCarry();
    wireZoom();
    wireCta();
    wireDepth();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
